<template>
  <div class="app-navbar">
    <div class="navbar-left">
      <AppBreadcrumbs :root="practiceName" :breadcrumbs="breadcrumbs" />
    </div>
    <div class="navbar-right">
      <div class="user-avatar-container" ref="avatarContainer">
        <div class="user-avatar-button" @click.prevent="toggleMenu" ref="avatar">
          <IconCircle :initials="userInitial" bg-color="var(--icon-user-bg)" color="var(--icon-user-color)"
            :size="22" />
          <span class="username-text">{{ userName }}</span>
        </div>
        <Teleport to="body">
          <Transition name="menu-slide" mode="out-in">
            <div v-if="isMenuOpen" class="menu-wrapper" :style="menuStyle" :key="currentMenu">
              <UserMenu v-if="currentMenu === 'user'" :user-name="userName" :user-email="userEmail"
                @switch-practice="showPracticeSelector" @manage-practices="handleManagePractices" @manage-users="handleManageUsers" @logout="handleLogout" />
              <PracticeSelectorMenu v-else-if="currentMenu === 'practice'" :current-practice-name="practiceName"
                :available-practices="practices" @select-practice="handleSelectPractice"
                @create-new="handleCreateNewPractice" />
            </div>
          </Transition>
        </Teleport>
      </div>
    </div>

    <!-- Modals -->
    <transition name="modal-fade" mode="out-in">
      <div class="modal-background close-on-click" v-if="modal.open" @click="handleModalClick">
        <div class="modal-scroll close-on-click">
          <transition name="modal-pop" mode="out-in">
            <UserListModal v-if="modal.id == 'user-list'" @create-user="handleCreateUser" @edit-user="handleEditUser" @delete-user="handleDeleteUser" />
            <CreateUserModal v-else-if="modal.id == 'create-user'" @user-created="handleUserCreated" />
            <UpdateUserModal v-else-if="modal.id == 'update-user'" :user="modal.data.user" @user-updated="handleUserUpdated" />
            <DeleteUserModal v-else-if="modal.id == 'delete-user'" :user="modal.data.user" @user-deleted="handleUserDeleted" />
            <ManagePracticesModal v-else-if="modal.id == 'practice-list'" @create-practice="handleCreateNewPractice" @edit-practice="handleEditPractice" />
            <CreatePracticeModal v-else-if="modal.id == 'create-practice'" @practice-created="handlePracticeCreated" />
            <UpdatePracticeModal v-else-if="modal.id == 'update-practice'" :practice="modal.data.practice" @practice-updated="handlePracticeUpdated" />
          </transition>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import * as api from '@/api'
import { queryClient } from '@/libs/queryClient'
import { alertService } from '@/libs/alert'
import AppBreadcrumbs from './AppBreadcrumbs.vue';
import UserMenu from '../menus/UserMenu.vue';
import PracticeSelectorMenu from '../menus/PracticeSelectorMenu.vue';
import IconCircle from '../common/IconCircle.vue';
import UserListModal from '../../modals/UserListModal.vue';
import CreateUserModal from '../../modals/CreateUserModal.vue';
import UpdateUserModal from '../../modals/UpdateUserModal.vue';
import DeleteUserModal from '../../modals/DeleteUserModal.vue';
import ManagePracticesModal from '../../modals/ManagePracticesModal.vue';
import CreatePracticeModal from '../../modals/CreatePracticeModal.vue';
import UpdatePracticeModal from '../../modals/UpdatePracticeModal.vue';

export default {
  name: 'AppNavbar',
  props: {
    breadcrumbs: {
      type: Array,
      required: true
    }
  },
  computed: {
    userName() {
      return this.user?.name || 'User';
    },
    userEmail() {
      return this.user?.email || '';
    },
    practiceName() {
      const currentPracticeId = api.getPracticeId();
      if (!currentPracticeId || !this.practices) return 'No practice selected';

      const practice = this.practices.find(p => p.practiceId === parseInt(currentPracticeId));
      return practice?.name || 'No practice selected';
    },
    userInitial() {
      return this.userName ? this.userName.charAt(0).toUpperCase() : 'U';
    },
    menuStyle() {
      return {
        top: `${this.menuPosition.top}px`,
        right: `${this.menuPosition.right}px`
      };
    },
  },
  data() {
    return {
      isLoading: false,
      practices: [],
      user: null,

      isMenuOpen: false,
      currentMenu: 'user', // 'user' or 'practice'
      menuPosition: { top: 0, right: 0 },


      modal: {
        open: false,
        id: '',
        data: {}
      }
    };
  },
  created() {
    this.load();
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    async load() {
      try {
        this.isLoading = true;

        this.user = queryClient.getQueryData(['user']) || null;

        const practicesResponse = await queryClient.fetchQuery({
          queryKey: ['practices'],
          queryFn: () => api.fetchPractices()
        });

        this.practices = practicesResponse;

      } catch (err) {
        console.error('Failed to fetch practices:', err);
        alertService.error('Failed to load account info: ' + err.message);
      } finally {
        this.isLoading = false;
      }
    },
    toggleMenu() {
      if (!this.isMenuOpen) {
        const avatar = this.$refs.avatar;
        if (avatar) {
          const rect = avatar.getBoundingClientRect();
          this.menuPosition = {
            top: rect.bottom + 8,
            right: window.innerWidth - rect.right
          };
        }
        this.currentMenu = 'user';
      }
      this.isMenuOpen = !this.isMenuOpen;
    },
    handleClickOutside(event) {
      const avatarContainer = this.$refs.avatarContainer;

      const clickedAvatar = avatarContainer && avatarContainer.contains(event.target);
      const clickedMenu = event.target.closest('.menu-wrapper');

      if (!clickedAvatar && !clickedMenu) {
        this.isMenuOpen = false;
        this.currentMenu = 'user';
      }
    },
    showPracticeSelector() {
      this.currentMenu = 'practice';
    },
    showUserMenu() {
      this.currentMenu = 'user';
    },
    async handleLogout() {
      try {
        await api.logout();
        api.setPracticeId(null);
        alertService.success('Logged out successfully');
        this.$router.push('/login');
      } catch (error) {
        console.error('Logout failed:', error);
        alertService.error('Logout failed: ' + error.message);
      } finally {
        this.isMenuOpen = false;
        this.currentMenu = 'user';
      }
    },
    handleSelectPractice(practiceId) {
      console.info('Switching practice to: ' + practiceId);
      api.setPracticeId(practiceId);
      this.isMenuOpen = false;
      this.currentMenu = 'user';
      window.location.reload();
    },
    handleCreateNewPractice() {
      this.isMenuOpen = false;
      this.currentMenu = 'user';
      this.openModal('create-practice', {});
    },
    handlePracticeCreated(practice) {
      this.closeModal();
      queryClient.invalidateQueries({ queryKey: ['practices'] });
      this.practices.push(practice);
      alertService.success('Practice created successfully');
      this.handleSelectPractice(practice.practiceId);
    },
    handleManagePractices() {
      this.isMenuOpen = false;
      this.currentMenu = 'user';
      this.openModal('practice-list', {});
    },
    handleManageUsers() {
      this.isMenuOpen = false;
      this.currentMenu = 'user';
      this.openModal('user-list', {});
    },
    openModal(id, data) {
      this.modal.open = true;

      setTimeout(() => {
        this.modal.id = id;
        this.modal.data = data;
      })
    },
    closeModal() {
      this.modal.id = '';

      setTimeout(() => {
        this.modal.open = false;
      });
    },
    handleModalClick(event) {
      const classList = event.target.classList;
      if (classList.contains('close-on-click')) {
        this.closeModal();
      }
    },
    handleCreateUser() {
      this.openModal('create-user', {});
    },
    handleEditUser(user) {
      this.openModal('update-user', { user });
    },
    handleDeleteUser(user) {
      this.openModal('delete-user', { user });
    },
    handleUserCreated() {
      this.closeModal();
      queryClient.invalidateQueries({ queryKey: ['users'] });
      alertService.success('User created successfully');
      this.openModal('user-list', {});
    },
    handleUserUpdated() {
      this.closeModal();
      queryClient.invalidateQueries({ queryKey: ['users'] });
      alertService.success('User updated successfully');
      this.openModal('user-list', {});
    },
    handleUserDeleted() {
      this.closeModal();
      queryClient.invalidateQueries({ queryKey: ['users'] });
      alertService.success('User deleted successfully');
      this.openModal('user-list', {});
    },
    handleEditPractice(practice) {
      this.openModal('update-practice', { practice });
    },
    async handlePracticeUpdated() {
      queryClient.invalidateQueries({ queryKey: ['practices'] });
      await this.load();
      alertService.success('Practice updated successfully');
      this.openModal('practice-list', {});
    }
  },
  components: { AppBreadcrumbs, UserMenu, PracticeSelectorMenu, IconCircle, UserListModal, CreateUserModal, UpdateUserModal, DeleteUserModal, ManagePracticesModal, CreatePracticeModal, UpdatePracticeModal }
}
</script>

<style scoped>
.app-navbar {
  position: relative;
  z-index: 999;
  width: 100%;
  height: var(--app-navbar-height);
  background: var(--app-navbar-bg);
  border-bottom: var(--app-navbar-border);
  box-shadow: var(--app-navbar-shadow);
  padding: 0px 16px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.navbar-right {
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.user-avatar-container {
  position: relative;
}

.user-avatar-button {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: var(--border-radius);
  transition: background-color 0.15s ease;
}

.user-avatar-button:hover {
  background-color: #f3f4f6;
}

.username-text {
  font-size: 14px;
  font-weight: 500;
  color: #262626;
}

/* Menu wrapper */
.menu-wrapper {
  position: fixed;
  z-index: 1000;
}

/* Menu expand animation - from top right */
.menu-slide-enter-active {
  transition: all 0.2s ease-out;
}

.menu-slide-leave-active {
  transition: all 0.15s ease-in;
}

.menu-slide-enter-from {
  opacity: 0;
  transform: scale(0.95);
  transform-origin: top right;
}

.menu-slide-leave-to {
  opacity: 0;
  transform: scale(0.95);
  transform-origin: top right;
}
</style>